/* eslint-disable no-unused-expressions */
/* eslint-disable no-console */
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import { v4 as uuid } from 'uuid';
import LogLevel from 'amazon-chime-sdk-js/build/logger/LogLevel';
import ConsoleLogger from 'amazon-chime-sdk-js/build/logger/ConsoleLogger';
import DefaultMessagingSession from '../lib/amazon-chime-sdk-js/build/messagingsession/DefaultMessagingSession';
import MessagingSessionConfiguration from 'amazon-chime-sdk-js/build/messagingsession/MessagingSessionConfiguration';
import { TokenExpiredError } from '../utils/backendFetch';
import PrefetchOn from 'amazon-chime-sdk-js/build/messagingsession/PrefetchOn';
import PrefetchSortBy from 'amazon-chime-sdk-js/build/messagingsession/PrefetchSortBy';
class MessagingService {
  constructor(
    chimeApi,
    onSessionStoppedHandler,
    onSessionTimeoutHandler,
    shouldPrefetch = true
  ) {
    this._chimeApi = chimeApi;
    this._session;
    this.sessionId = uuid();
    this._logger = new ConsoleLogger('不動産チャット', LogLevel.INFO);
    this._messageUpdateCallbacks = [];
    this._onSessionStoppedHandler = onSessionStoppedHandler;
    this._requestTimeout;
    this.REQUEST_TIMEOUT_MS = 5000; // タイムアウト時間が5秒
    this._onSessionTimeoutHandler = onSessionTimeoutHandler;
    this._shouldPrefetch = shouldPrefetch;
  }

  messageObserver = {
    messagingSessionDidStart: () => {
      console.log('Messaging Connection started!');
      if (this._requestTimeout) {
        clearTimeout(this._requestTimeout);
      }
      window.dispatchEvent(new Event('ws-start'));
    },
    messagingSessionDidStartConnecting: (reconnecting) => {
      console.log('Messaging Connection connecting');
      this._requestTimeout = setTimeout(() => {
        console.log('Websocket request timeout');
        this._onSessionTimeoutHandler?.();
      }, this.REQUEST_TIMEOUT_MS);
    },
    messagingSessionDidStop: (event) => {
      console.log('Messaging Connection received DidStop event');
      this._onSessionStoppedHandler?.(new TokenExpiredError());
    },
    messagingSessionDidReceiveMessage: (message) => {
      console.log('Messaging Connection received message');
      this.publishMessageUpdate(message);
    }
  };

  setMessagingEndpoint() {
    this._connection = true;
    this._chimeApi
      .getMessagingSessionEndpoint()
      .then(async (response) => {
        this._endpoint = response?.Endpoint?.Url;
        const client = await this._chimeApi.chimeMessagingClient();
        if (
          typeof client.config.credentials === 'object' &&
          typeof client.config.credentials.then === 'function'
        ) {
          client.config.credentials = await client.config.credentials;
        }
        const sessionConfig = new MessagingSessionConfiguration(
          this._chimeApi.createMemberArn(),
          this.sessionId,
          this._endpoint,
          client
        );

        if (this._shouldPrefetch) {
          sessionConfig.prefetchOn = PrefetchOn.Connect;
          // ソートが効いていないのでフロントで対応しています。
          sessionConfig.prefetchSortBy = PrefetchSortBy.LastMessageTimestamp;
        }

        this._session = new DefaultMessagingSession(
          sessionConfig,
          this._logger
        );

        this._session.addObserver(this.messageObserver);
        this._session.start();
      })
      .catch((err) => {
        console.error(err);
      });
  }

  connect() {
    if (!this._connection) {
      this.setMessagingEndpoint();
    }
  }

  close() {
    this._connection = false;
    try {
      this._session.stop();
    } catch (err) {
      console.error('Failed to stop Messaging Session.');
    }
  }

  subscribeToMessageUpdate(callback) {
    console.log('Message listener subscribed!');
    this._messageUpdateCallbacks.push(callback);
  }

  unsubscribeFromMessageUpdate(callback) {
    const index = this._messageUpdateCallbacks.indexOf(callback);
    if (index !== -1) {
      this._messageUpdateCallbacks.splice(index, 1);
    }
  }

  publishMessageUpdate(message) {
    console.log('Sending message update to listeners!');
    for (let i = 0; i < this._messageUpdateCallbacks.length; i += 1) {
      const callback = this._messageUpdateCallbacks[i];
      callback(message);
    }
  }
}

export default MessagingService;
